import {
    Badge,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    MenuItem,
    Stack,
    Tab,
    Tabs,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import CircleRounded from '@mui/icons-material/CircleRounded';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import HelpOutline from '@mui/icons-material/HelpOutline';
import SearchIcon from '@mui/icons-material/Search';
import { format, isValid, parseISO } from 'date-fns';
import { useConfirm } from 'material-ui-confirm';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useBenefitsQueueSocket } from '../../../hooks/useBenefitsQueueSocket';
import { Link } from 'react-router-dom';
import ListPageLayout from '../../../components/ListPageLayout';
import SelectClub from '../../../components/SelectClub';
import { useAuth } from '../../../context/AuthContext';
import { useFetch } from '../../../hooks/useFecth';
import { useSearchTextInput } from '../../../hooks/useSearchTextInput';
import useErrors from '../../../hooks/useErrors';
import server from '../../../services/server';

const CUTOFF_OPTIONS = [
    { value: '10', label: 'Dia 10' },
    { value: '20', label: 'Dia 20' },
    { value: 'last', label: 'Último dia do mês' },
];

function currentCycleMonth() {
    return new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Sao_Paulo',
        year: 'numeric',
        month: '2-digit',
    }).format(new Date());
}

function formatDate(value) {
    if (!value) return '—';
    try {
        const d = typeof value === 'string' ? parseISO(value) : new Date(value);
        return isValid(d) ? format(d, 'dd/MM/yyyy') : '—';
    } catch {
        return '—';
    }
}

function formatDateTime(value) {
    if (!value) return '—';
    try {
        const d = typeof value === 'number' ? new Date(value) : new Date(value);
        return isValid(d) ? format(d, 'dd/MM/yyyy HH:mm') : '—';
    } catch {
        return '—';
    }
}

function queueStateChip(state) {
    if (state === 'active') return { label: 'Processando', color: 'info' };
    if (state === 'waiting') return { label: 'Na fila', color: 'default' };
    if (state === 'delayed') return { label: 'Agendado', color: 'warning' };
    if (state === 'paused') return { label: 'Pausado', color: 'default' };
    if (state === 'failed') return { label: 'Falhou', color: 'error' };
    return { label: state || '—', color: 'default' };
}

function queueCutoffLabel(slot) {
    if (slot === '10') return 'Dia 10';
    if (slot === '20') return 'Dia 20';
    if (slot === 'last') return 'Último dia';
    return slot || '—';
}

function lastCalendarDayOfMonth(year, month) {
    return new Date(Date.UTC(Number(year), Number(month), 0)).getUTCDate();
}

function parseBenefitCycle(order) {
    const fromDataSlot = order?.orderData?.cutoffSlot;
    const fromDataMonth = order?.orderData?.cutoffMonth;
    if (fromDataSlot && fromDataMonth) {
        const [year, month] = String(fromDataMonth).split('-');
        return { slot: String(fromDataSlot), year, month };
    }
    const raw = String(order?.benefitCycleId || '').split('#')[0];
    const parts = raw.split('-');
    if (parts.length >= 4) {
        return { slot: parts[1], year: parts[2], month: parts[3] };
    }
    return null;
}

function cycleLabel(order) {
    const parsed = parseBenefitCycle(order);
    if (!parsed?.year || !parsed?.month || !parsed?.slot) return order?.benefitCycleId || '—';
    const year = Number(parsed.year);
    const month = Number(parsed.month);
    const day = parsed.slot === 'last' ? lastCalendarDayOfMonth(year, month) : Number(parsed.slot);
    if (!year || !month || !day) return order?.benefitCycleId || '—';
    return `${String(day).padStart(2, '0')}/${String(month).padStart(2, '0')}/${year}`;
}

function money(value) {
    return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function paymentLink(order) {
    return order?.paymentData?.invoiceUrl || order?.paymentData?.bankSlipUrl || null;
}

function isPaidOrder(order) {
    return ['CONFIRMED', 'RECEIVED', 'RECEIVED_IN_CASH'].includes(order?.paymentStatus) || order?.status === 'CONCLUDED';
}

function isCanceledOrder(order) {
    return Boolean(order?.canceledAt) || order?.status === 'CANCELED' || order?.paymentStatus === 'CANCELED';
}

function canMutateOrder(order) {
    return order?.type === 'benefits' && !isPaidOrder(order) && !isCanceledOrder(order);
}

function paymentStatusChip(order) {
    if (isCanceledOrder(order)) {
        return { label: 'Cancelada', color: 'default' };
    }
    if (!order?.paymentId && order?.type === 'benefits') {
        return { label: 'Sem boleto', color: 'warning' };
    }
    const status = String(order?.paymentStatus || '').toUpperCase();
    if (['CONFIRMED', 'RECEIVED', 'RECEIVED_IN_CASH'].includes(status) || order?.status === 'CONCLUDED') {
        return { label: 'Paga', color: 'success' };
    }
    if (status === 'OVERDUE') return { label: 'Atrasada', color: 'error' };
    if (status === 'REFUNDED') return { label: 'Estornada', color: 'default' };
    if (status === 'PENDING') return { label: 'Pendente', color: 'warning' };
    if (status === 'WAITING') return { label: 'Aguardando', color: 'info' };
    return { label: order?.paymentStatus || '—', color: 'default' };
}

function toDateInput(value) {
    if (!value) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(String(value))) return String(value);
    try {
        const d = typeof value === 'string' ? parseISO(value) : new Date(value);
        return isValid(d) ? format(d, 'yyyy-MM-dd') : '';
    } catch {
        return '';
    }
}

function chargeDescription(order) {
    return order?.paymentData?.description || order?.orderData?.description || '';
}

function chargeDueDate(order) {
    return toDateInput(order?.paymentData?.dueDate || order?.dueDateCycle);
}

function itemBenefitName(item) {
    return item?.Benefit?.aditionalName || item?.name || 'Benefício';
}

function itemLabel(item) {
    const person = item?.Benefit?.person?.name;
    const benefit = itemBenefitName(item);
    return person ? `${person} · ${benefit}` : benefit;
}

function itemPerson(item) {
    const person = item?.Benefit?.person;
    return {
        key: person?.id || item?.Benefit?.personId || `item-${item.id}`,
        name: person?.name || 'Sem beneficiário',
        document: person?.document || '',
        personId: person?.id || item?.Benefit?.personId || null,
    };
}

function groupOrderItems(items, query) {
    const q = String(query || '').trim().toLowerCase();
    const filtered = (items || []).filter(item => {
        if (!q) return true;
        const person = itemPerson(item);
        return [person.name, person.document, itemBenefitName(item), String(item.benefitId || ''), String(item.id)]
            .join(' ')
            .toLowerCase()
            .includes(q);
    });

    const groups = new Map();
    for (const item of filtered) {
        const person = itemPerson(item);
        if (!groups.has(person.key)) {
            groups.set(person.key, { ...person, items: [], total: 0 });
        }
        const group = groups.get(person.key);
        group.items.push(item);
        group.total += Number(item.total || 0);
    }

    return [...groups.values()].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
}

function HelpSection({ title, children }) {
    return (
        <Box>
            <Typography fontWeight={700} sx={{ mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div">
                {children}
            </Typography>
        </Box>
    );
}

function BenefitsOrdersHelpDialog({ open, onClose }) {
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Como usar as cobranças de benefícios</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2.5}>
                    <HelpSection title="O que é esta tela">
                        Aqui ficam as mensalidades de benefícios (telemedicina, seguro, telepsicologia etc.)
                        cobradas dos clubes. Cada card é uma cobrança de um clube, em um corte do mês.
                    </HelpSection>
                    <HelpSection title="Quando as cobranças nascem">
                        O sistema gera sozinho nos cortes do mês: dia 10, dia 20 e último dia.
                        Entram só benefícios ativos, sem cancelamento, com produto de revenda e no corte certo.
                        Quem tem permissão de super também pode usar <strong>Rodar cobrança</strong> para gerar na hora.
                    </HelpSection>
                    <HelpSection title="O que o chip de status significa">
                        <strong>Sem boleto</strong> — o pedido existe, mas o boleto ainda não foi emitido (muitas vezes falta CNPJ do clube).
                        <br />
                        <strong>Aguardando / Pendente</strong> — boleto emitido, ainda sem pagamento.
                        <br />
                        <strong>Atrasada</strong> — passou do vencimento.
                        <br />
                        <strong>Paga</strong> — já recebida. Não dá para alterar.
                        <br />
                        <strong>Cancelada</strong> — pedido e boleto foram cancelados.
                    </HelpSection>
                    <HelpSection title="Como abrir e o que tem dentro">
                        Clique no card. Em <strong>Informações</strong> estão valor, vencimento, ciclo e texto do boleto.
                        Em <strong>Beneficiários</strong> estão as pessoas cobradas, agrupadas por nome. Dá para buscar.
                    </HelpSection>
                    <HelpSection title="Remover alguém que entrou errado">
                        Na aba Beneficiários, use a lixeira do item. O valor da cobrança é recalculado na hora.
                        Se já existir boleto, o valor no Asaas também muda. Esse item não volta neste ciclo.
                        Se a pessoa não deveria ser cobrada no próximo corte, inative o benefício na tela de providers.
                    </HelpSection>
                    <HelpSection title="Editar a cobrança">
                        Use <strong>Editar cobrança</strong> para mudar valor, vencimento ou descrição.
                        Se já houver boleto, a mudança vai para o Asaas. Se ainda não houver, fica só no pedido até emitir.
                    </HelpSection>
                    <HelpSection title="Cancelar a cobrança">
                        Use <strong>Cancelar cobrança</strong> para encerrar o pedido e apagar o boleto no Asaas.
                        Depois disso, o mesmo corte pode ser gerado de novo.
                    </HelpSection>
                    <HelpSection title="Emitir, resync e link">
                        <strong>Emitir</strong> tenta criar o boleto (precisa do CNPJ do clube).
                        <strong>Resync</strong> atualiza o status com o Asaas.
                        <strong>Link de pagamento</strong> abre o boleto.
                        <strong>Clube</strong> vai para o cadastro do clube.
                    </HelpSection>
                    <HelpSection title="Fila">
                        O botão <strong>Fila</strong> mostra os clubes que estão gerando cobrança agora:
                        processando, esperando, agendados para as 22h ou com falha. A lista atualiza em
                        tempo real pelo socket.
                    </HelpSection>
                    <HelpSection title="O que não dá para fazer">
                        Cobrança já paga ou já cancelada não permite remover item, editar nem cancelar de novo.
                    </HelpSection>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Entendi</Button>
            </DialogActions>
        </Dialog>
    );
}

function BenefitsQueueDialog({ open, onClose, queue, loading, connected }) {
    const counts = queue?.counts || {};
    const jobs = queue?.jobs || [];

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                    Fila de cobrança
                    <Tooltip title={connected ? 'Sala benefits:queue' : 'Socket desconectado'}>
                        <Chip
                            size="small"
                            icon={<CircleRounded sx={{ fontSize: '10px !important' }} />}
                            label={connected ? 'Ao vivo' : 'Offline'}
                            color={connected ? 'success' : 'default'}
                            variant={connected ? 'filled' : 'outlined'}
                        />
                    </Tooltip>
                </Stack>
                <Typography variant="body2" color="text.secondary" fontWeight={400}>
                    Clubes sendo processados agora. Atualiza em tempo real.
                </Typography>
            </DialogTitle>
            <DialogContent dividers>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                    <Chip size="small" color="info" label={`Processando ${counts.active || 0}`} />
                    <Chip size="small" label={`Na fila ${counts.waiting || 0}`} />
                    <Chip size="small" color="warning" label={`Agendados ${counts.delayed || 0}`} />
                    <Chip size="small" color="error" label={`Falhas ${counts.failed || 0}`} />
                </Stack>
                {loading && jobs.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center" py={3}>
                        Carregando fila…
                    </Typography>
                ) : jobs.length === 0 ? (
                    <Typography color="text.secondary" textAlign="center" py={3}>
                        Nenhum job na fila neste momento.
                    </Typography>
                ) : (
                    <Stack spacing={1.25}>
                        {jobs.map(job => (
                            <Box key={job.id} sx={{ py: 0.5 }}>
                                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography fontWeight={700} noWrap>
                                            {job.clubName}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {queueCutoffLabel(job.cutoffSlot)} · {job.cycleMonth || '—'} · {formatDateTime(job.timestamp)}
                                        </Typography>
                                        {job.failedReason && (
                                            <Typography variant="caption" color="error" display="block">
                                                {job.failedReason}
                                            </Typography>
                                        )}
                                    </Box>
                                    <Chip size="small" {...queueStateChip(job.state)} />
                                </Stack>
                                <Divider sx={{ mt: 1 }} />
                            </Box>
                        ))}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Fechar</Button>
            </DialogActions>
        </Dialog>
    );
}

export default function AdminOrders() {
    const { user } = useAuth();
    const isSuper = Boolean(user?.super);
    const { search, input, handleChange } = useSearchTextInput();
    const { enqueueSnackbar, setErrors } = useErrors();
    const confirm = useConfirm();
    const [clubId, setClubId] = useState(null);
    const [type, setType] = useState('benefits');
    const [missingPayment, setMissingPayment] = useState('');
    const [page, setPage] = useState(1);
    const [selected, setSelected] = useState(null);
    const [detailTab, setDetailTab] = useState(0);
    const [itemQuery, setItemQuery] = useState('');
    const [busy, setBusy] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [editForm, setEditForm] = useState({ value: '', description: '', dueDate: '' });
    const [helpOpen, setHelpOpen] = useState(false);
    const [queueOpen, setQueueOpen] = useState(false);
    const [queue, setQueue] = useState({ counts: {}, jobs: [] });
    const [queueLoading, setQueueLoading] = useState(false);
    const [runOpen, setRunOpen] = useState(false);
    const [cutoffSlot, setCutoffSlot] = useState('10');
    const [cycleMonth, setCycleMonth] = useState(currentCycleMonth);
    const [running, setRunning] = useState(false);

    const filters = useMemo(() => ({
        ...(search ? { benefitCycleId: search } : {}),
        ...(clubId ? { clubId } : {}),
        ...(type ? { type } : {}),
        ...(missingPayment ? { missingPayment: 'true' } : {}),
        page,
        limit: 20,
    }), [search, clubId, type, missingPayment, page]);

    const { data, isLoading, mutate } = useFetch('orders', { params: filters });
    const rows = data?.data ?? [];
    const missingPaymentCount = data?.missingPaymentCount ?? 0;

    const runAction = async (order, action) => {
        try {
            setBusy(true);
            if (action === 'resync') await server.put(`orders/${order.id}/resync`);
            if (action === 'emit') await server.post(`orders/${order.id}/emit`);
            enqueueSnackbar(action === 'resync' ? 'Cobrança sincronizada.' : 'Emissão enviada.');
            await mutate();
            if (selected?.id === order.id) {
                const { data: body } = await server.get(`orders/${order.id}`);
                setSelected(body.data);
            }
        } catch (error) {
            setErrors(error);
        } finally {
            setBusy(false);
        }
    };

    const runManualCharge = async () => {
        if (!CUTOFF_OPTIONS.some(item => item.value === cutoffSlot) || !cycleMonth) {
            enqueueSnackbar('Selecione o dia de corte e o mês do ciclo.');
            return;
        }
        try {
            setRunning(true);
            const { data: body } = await server.post('benefits/run', { cutoffSlot, cycleMonth });
            enqueueSnackbar(body?.message || 'Cobrança enfileirada.');
            setRunOpen(false);
            setQueueOpen(true);
            await mutate();
        } catch (error) {
            setErrors(error);
        } finally {
            setRunning(false);
        }
    };

    const loadQueue = useCallback(async ({ silent = false } = {}) => {
        try {
            if (!silent) setQueueLoading(true);
            const { data: body } = await server.get('benefits/queue');
            setQueue(body.data || { counts: {}, jobs: [] });
        } catch (error) {
            if (!silent) setErrors(error);
        } finally {
            setQueueLoading(false);
        }
    }, [setErrors]);

    useEffect(() => {
        loadQueue({ silent: true });
    }, [loadQueue]);

    useEffect(() => {
        if (queueOpen) loadQueue();
    }, [queueOpen, loadQueue]);

    const onSocketUpdate = useCallback(payload => {
        if (payload?.counts && Array.isArray(payload.jobs)) {
            setQueue({ counts: payload.counts, jobs: payload.jobs });
            setQueueLoading(false);
            return;
        }
        loadQueue({ silent: true });
    }, [loadQueue]);

    const { connected } = useBenefitsQueueSocket({ onUpdate: onSocketUpdate });

    const openDetail = async order => {
        try {
            const { data: body } = await server.get(`orders/${order.id}`);
            setItemQuery('');
            setDetailTab(0);
            setSelected(body.data);
            return body.data;
        } catch (error) {
            setErrors(error);
            return null;
        }
    };

    const closeDetail = () => {
        if (busy) return;
        setSelected(null);
        setItemQuery('');
        setDetailTab(0);
        setEditOpen(false);
    };

    const openEdit = order => {
        const current = order || selected;
        if (!current) return;
        setEditForm({
            value: Number(current.total || 0),
            description: chargeDescription(current),
            dueDate: chargeDueDate(current),
        });
        setEditOpen(true);
    };

    const saveCharge = async () => {
        if (!selected) return;
        try {
            setBusy(true);
            const { data: body } = await server.put(`orders/${selected.id}`, {
                value: editForm.value,
                description: editForm.description,
                dueDate: editForm.dueDate || undefined,
            });
            setSelected(body.data);
            setEditOpen(false);
            enqueueSnackbar(body?.message || 'Cobrança atualizada.');
            await mutate();
        } catch (error) {
            setErrors(error);
        } finally {
            setBusy(false);
        }
    };

    const cancelOrder = async order => {
        try {
            await confirm({
                title: 'Cancelar cobrança',
                description: 'Cancelar este pedido e a cobrança no Asaas? O ciclo fica livre para uma nova geração.',
                confirmationText: 'Cancelar cobrança',
                confirmationButtonProps: { color: 'error' },
            });
            setBusy(true);
            const { data: body } = await server.post(`orders/${order.id}/cancel`);
            setSelected(body.data);
            enqueueSnackbar(body?.message || 'Cobrança cancelada.');
            await mutate();
        } catch (error) {
            if (error !== undefined) setErrors(error);
        } finally {
            setBusy(false);
        }
    };

    const groupedItems = useMemo(
        () => groupOrderItems(selected?.Order_products, itemQuery),
        [selected, itemQuery],
    );

    const removeItem = async (order, item) => {
        try {
            await confirm({
                title: 'Remover item da cobrança',
                description: `Remover ${itemLabel(item)} desta cobrança? O total será recalculado e o item não volta neste ciclo.`,
                confirmationText: 'Remover',
                confirmationButtonProps: { color: 'error' },
            });
            setBusy(true);
            const { data: body } = await server.delete(`orders/${order.id}/items/${item.id}`);
            setSelected(body.data);
            enqueueSnackbar('Item removido da cobrança.');
            await mutate();
        } catch (error) {
            if (error !== undefined) setErrors(error);
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
        <ListPageLayout
            title="Benefícios"
            subtitle="Mensalidades de benefícios cobradas dos clubes."
            counts={[
                { label: 'cobranças', value: data?.total ?? rows.length, color: 'primary' },
                { label: 'sem boleto', value: missingPaymentCount, color: 'warning' },
            ]}
            loading={isLoading && !data}
            filterActive={Boolean(search || clubId || missingPayment || (type && type !== 'benefits'))}
            onClearFilters={() => {
                setClubId(null);
                setType('benefits');
                setMissingPayment('');
                setPage(1);
            }}
            actions={
                <Stack direction="row" spacing={1} alignItems="center">
                    <Tooltip title="Como usar esta tela">
                        <IconButton size="small" aria-label="Ajuda" onClick={() => setHelpOpen(true)}>
                            <HelpOutline />
                        </IconButton>
                    </Tooltip>
                    <Badge
                        color="info"
                        badgeContent={queue?.counts?.pending || 0}
                        invisible={!queue?.counts?.pending}
                    >
                        <Tooltip title={connected ? 'Fila ao vivo' : 'Socket desconectado — abra para atualizar'}>
                            <Button size="small" variant="outlined" onClick={() => setQueueOpen(true)}>
                                Fila
                            </Button>
                        </Tooltip>
                    </Badge>
                    {isSuper ? (
                        <Button size="small" variant="contained" onClick={() => setRunOpen(true)}>
                            Rodar cobrança
                        </Button>
                    ) : null}
                </Stack>
            }
            filterPanel={() => (
                <Stack spacing={2}>
                    <TextField
                        label="benefitCycleId"
                        placeholder="12-10-2026-09"
                        fullWidth
                        value={input}
                        onChange={e => handleChange(e.target.value)}
                    />
                    <SelectClub value={clubId} onChange={e => setClubId(e.target.value)} />
                    <TextField select label="Tipo" value={type} onChange={e => setType(e.target.value)} fullWidth>
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="benefits">Benefícios</MenuItem>
                    </TextField>
                    <TextField
                        select
                        label="Alerta"
                        value={missingPayment}
                        onChange={e => setMissingPayment(e.target.value)}
                        fullWidth
                    >
                        <MenuItem value="">Todos</MenuItem>
                        <MenuItem value="1">Sem boleto (benefits)</MenuItem>
                    </TextField>
                </Stack>
            )}
            pagination={{
                page: data?.page ?? page,
                pages: data?.totalPages || 1,
                total: data?.total ?? rows.length,
                pageSize: 20,
                onChange: (_, next) => setPage(next),
                itemLabel: 'cobranças',
            }}
        >
            {() => (
                <Stack spacing={2} sx={{ p: 2 }}>
                    {rows.map(order => (
                            <Card
                                key={order.id}
                                variant="outlined"
                                onClick={() => openDetail(order)}
                                sx={{
                                    cursor: 'pointer',
                                    borderColor: isCanceledOrder(order)
                                        ? 'action.disabled'
                                        : !order.paymentId && order.type === 'benefits'
                                          ? 'warning.main'
                                          : undefined,
                                    opacity: isCanceledOrder(order) ? 0.72 : 1,
                                }}
                            >
                                <CardContent>
                                    <Stack spacing={1}>
                                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                                            <Typography fontWeight={700} noWrap>
                                                {order.Club?.name || '—'} · Ciclo {cycleLabel(order)}
                                            </Typography>
                                            <Chip size="small" {...paymentStatusChip(order)} />
                                        </Stack>
                                        <Typography variant="body2">
                                            #{order.id} · {money(order.total)}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatDate(order.createdAt)}
                                        </Typography>
                                    </Stack>
                                </CardContent>
                            </Card>
                    ))}
                    {!isLoading && rows.length === 0 && (
                        <Typography color="text.secondary" textAlign="center">
                            Nenhuma cobrança encontrada.
                        </Typography>
                    )}
                </Stack>
            )}
        </ListPageLayout>
            <Dialog open={Boolean(selected)} onClose={closeDetail} fullWidth maxWidth="md">
                <DialogTitle sx={{ pb: 0 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <Box>
                            Cobrança #{selected?.id}
                            <Typography variant="body2" color="text.secondary" fontWeight={400}>
                                {selected?.Club?.name || '—'} · {money(selected?.total)}
                            </Typography>
                        </Box>
                        {selected && <Chip size="small" {...paymentStatusChip(selected)} />}
                    </Stack>
                </DialogTitle>
                <Tabs
                    value={detailTab}
                    onChange={(_, next) => setDetailTab(next)}
                    sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab label="Informações" />
                    <Tab label={`Beneficiários (${selected?.Order_products?.length || 0})`} />
                </Tabs>
                <DialogContent dividers>
                    {detailTab === 0 && (
                        <Stack spacing={1.5}>
                            <Typography variant="body2"><strong>Clube:</strong> {selected?.Club?.name || '—'}</Typography>
                            <Typography variant="body2"><strong>Valor:</strong> {money(selected?.total)}</Typography>
                            <Typography variant="body2">
                                <strong>Pagamento:</strong> {paymentStatusChip(selected).label}
                                {selected?.paymentId ? ` · ${selected.paymentId}` : ''}
                            </Typography>
                            <Typography variant="body2"><strong>Ciclo:</strong> {cycleLabel(selected)}</Typography>
                            <Typography variant="body2">
                                <strong>Vencimento:</strong>{' '}
                                {chargeDueDate(selected) ? formatDate(chargeDueDate(selected)) : '—'}
                            </Typography>
                            <Typography variant="body2"><strong>Criado:</strong> {formatDate(selected?.createdAt)}</Typography>
                            <Typography variant="body2">
                                <strong>Itens:</strong> {selected?.Order_products?.length || 0}
                            </Typography>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                <strong>Descrição:</strong> {chargeDescription(selected) || '—'}
                            </Typography>
                        </Stack>
                    )}
                    {detailTab === 1 && (
                        <>
                            <TextField
                                size="small"
                                fullWidth
                                placeholder="Buscar beneficiário, documento ou benefício"
                                value={itemQuery}
                                onChange={e => setItemQuery(e.target.value)}
                                sx={{ mb: 2 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            {groupedItems.length === 0 ? (
                                <Typography color="text.secondary" textAlign="center" py={3}>
                                    Nenhum item encontrado.
                                </Typography>
                            ) : (
                                <Stack spacing={2}>
                                    {groupedItems.map(group => (
                                        <Box key={group.key}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
                                                <Box>
                                                    <Typography fontWeight={700}>{group.name}</Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {group.document || 'sem documento'} · {group.items.length} benefício(s)
                                                    </Typography>
                                                </Box>
                                                <Typography variant="body2" fontWeight={700}>
                                                    {money(group.total)}
                                                </Typography>
                                            </Stack>
                                            <List disablePadding dense>
                                                {group.items.map(item => (
                                                    <ListItem
                                                        key={item.id}
                                                        disableGutters
                                                        secondaryAction={
                                                            canMutateOrder(selected) ? (
                                                                <IconButton
                                                                    edge="end"
                                                                    size="small"
                                                                    aria-label="Remover item"
                                                                    disabled={busy}
                                                                    onClick={() => removeItem(selected, item)}
                                                                >
                                                                    <DeleteOutline fontSize="small" />
                                                                </IconButton>
                                                            ) : null
                                                        }
                                                    >
                                                        <ListItemText
                                                            primary={itemBenefitName(item)}
                                                            secondary={`#${item.benefitId || item.id} · ${money(item.total)}`}
                                                        />
                                                    </ListItem>
                                                ))}
                                            </List>
                                            <Divider sx={{ mt: 1 }} />
                                        </Box>
                                    ))}
                                </Stack>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ flexWrap: 'wrap', gap: 1, justifyContent: 'flex-start' }}>
                    {paymentLink(selected) && (
                        <Button
                            size="small"
                            component="a"
                            href={paymentLink(selected)}
                            target="_blank"
                            rel="noreferrer"
                        >
                            Link de pagamento
                        </Button>
                    )}
                    {selected?.paymentId && (
                        <Button size="small" disabled={busy} onClick={() => runAction(selected, 'resync')}>
                            Resync
                        </Button>
                    )}
                    {canMutateOrder(selected) && !selected?.paymentId && (
                        <Button size="small" disabled={busy} onClick={() => runAction(selected, 'emit')}>
                            Emitir
                        </Button>
                    )}
                    {canMutateOrder(selected) && (
                        <>
                            <Button size="small" disabled={busy} onClick={() => openEdit(selected)}>
                                Editar cobrança
                            </Button>
                            <Button size="small" color="error" disabled={busy} onClick={() => cancelOrder(selected)}>
                                Cancelar cobrança
                            </Button>
                        </>
                    )}
                    {selected?.clubId && (
                        <Button size="small" component={Link} to={`/dashboard/clubes/${selected.clubId}`}>
                            Clube
                        </Button>
                    )}
                    <Box sx={{ flex: 1 }} />
                    <Button onClick={closeDetail} disabled={busy}>
                        Fechar
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog open={editOpen} onClose={() => !busy && setEditOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Editar cobrança</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} pt={1}>
                        <Typography variant="body2" color="text.secondary">
                            {selected?.paymentId
                                ? 'Valor, descrição e vencimento serão atualizados no Asaas.'
                                : 'Ainda não há boleto. Os dados ficam no pedido até emitir.'}
                        </Typography>
                        <TextField
                            label="Valor"
                            type="number"
                            inputProps={{ min: 0, step: '0.01' }}
                            value={editForm.value}
                            onChange={e => setEditForm(prev => ({ ...prev, value: e.target.value }))}
                            fullWidth
                        />
                        <TextField
                            label="Vencimento"
                            type="date"
                            value={editForm.dueDate}
                            onChange={e => setEditForm(prev => ({ ...prev, dueDate: e.target.value }))}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                        />
                        <TextField
                            label="Descrição"
                            value={editForm.description}
                            onChange={e => setEditForm(prev => ({ ...prev, description: e.target.value }))}
                            multiline
                            minRows={3}
                            fullWidth
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditOpen(false)} disabled={busy}>
                        Voltar
                    </Button>
                    <Button variant="contained" onClick={saveCharge} disabled={busy}>
                        {busy ? 'Salvando…' : 'Salvar'}
                    </Button>
                </DialogActions>
            </Dialog>
            {isSuper ? (
                <Dialog open={runOpen} onClose={() => !running && setRunOpen(false)} fullWidth maxWidth="xs">
                    <DialogTitle>Rodar cobrança de benefícios</DialogTitle>
                    <DialogContent>
                        <Stack spacing={2} pt={1}>
                            <Typography variant="body2" color="text.secondary">
                                Enfileira a cobrança de todos os clubes elegíveis neste corte, no mesmo fluxo do job das 22h.
                            </Typography>
                            <TextField
                                select
                                label="Dia de corte"
                                value={cutoffSlot}
                                onChange={e => setCutoffSlot(e.target.value)}
                                fullWidth
                            >
                                {CUTOFF_OPTIONS.map(item => (
                                    <MenuItem key={item.value} value={item.value}>
                                        {item.label}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                type="month"
                                label="Mês do ciclo"
                                value={cycleMonth}
                                onChange={e => setCycleMonth(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </Stack>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setRunOpen(false)} disabled={running}>
                            Cancelar
                        </Button>
                        <Button variant="contained" onClick={runManualCharge} disabled={running}>
                            {running ? 'Enfileirando...' : 'Rodar agora'}
                        </Button>
                    </DialogActions>
                </Dialog>
            ) : null}
            <BenefitsOrdersHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
            <BenefitsQueueDialog
                open={queueOpen}
                onClose={() => setQueueOpen(false)}
                queue={queue}
                loading={queueLoading}
                connected={connected}
            />
        </>
    );
}
